/**
 * Repo news — the nightly runner's per-run digest (`tools/cache/news.jsonl`).
 *
 * Append-only JSONL, oldest first; each line is one LLM-written digest:
 * an overall line plus one paragraph per domain that says what changed and
 * what waits. The console only reads this file (the runner owns it).
 */

export interface NewsDomain {
  domain: string;
  body: string;
}

export interface NewsEntry {
  generatedAt: string;
  agents: string;
  overall: string;
  domains: NewsDomain[];
}

/** Parse the JSONL file; invalid lines are skipped, empty entries dropped. */
export function parseNewsFile(text: string): NewsEntry[] {
  const out: NewsEntry[] = [];
  for (const raw of text.split(/\r?\n/)) {
    const line = raw.trim();
    if (!line) continue;
    let data: unknown;
    try {
      data = JSON.parse(line);
    } catch {
      continue;
    }
    if (typeof data !== "object" || data === null) continue;
    const d = data as Record<string, unknown>;
    const domains = Array.isArray(d.domains)
      ? d.domains
          .filter((x): x is Record<string, unknown> => typeof x === "object" && x !== null)
          .map((x) => ({ domain: String(x.domain ?? ""), body: String(x.body ?? "") }))
          .filter((x) => x.domain !== "" && x.body !== "")
      : [];
    const overall = String(d.overall ?? "");
    if (!overall && domains.length === 0) continue;
    out.push({
      generatedAt: String(d.generated_at ?? ""),
      agents: String(d.agents ?? ""),
      overall,
      domains,
    });
  }
  return out;
}

/** Newest digest (the file is append-only, oldest first). */
export function latestNews(entries: NewsEntry[]): NewsEntry | null {
  return entries.length > 0 ? entries[entries.length - 1] : null;
}

/** True when the newest digest arrived after `seenAt` ("" = never seen). */
export function hasUnseenNews(entries: NewsEntry[], seenAt: string): boolean {
  const latest = latestNews(entries);
  if (!latest || !latest.generatedAt) return false;
  return seenAt === "" || latest.generatedAt > seenAt;
}
